import {
    ChildTextContentElement,
    OptionItemState,
    TextContentElement,
} from '@deltek/specification-client/core/Models/textContentElement'
import { removeUnitOfMeasureDelimiters } from 'components/WorkspaceEditor/components/NodeContents/hooks/useFormatReplacementOption'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types/contentItemViewNode.type'
import { splitAnnotatedElementsAtOffset } from 'components/WorkspaceEditor/utils/splitAnnotatedElementsAtOffset'
import { hasUOMContent } from 'components/WorkspaceEditor/utils/uomUtils'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { v4 as newGuid } from 'uuid'
import {
    PROPERTY_EDITOR_PILL_STATE_VARIANT as PillStateVariants,
    PILL_TRACK_CHANGE_STATE as PillTrackChangeState,
} from '../constants'

export { splitAnnotatedElementsAtOffset }

// ─── Pure tree-transform helpers for pill insertion ───────────────────────────

const DEFAULT_TEXT_AFTER_PILL = ' '
const isWhitespaceOnly = (text: string): boolean => /^[​﻿\s]*$/.test(text)

export interface BuildNodesParams {
    currentNodes: Partial<ContentItemViewNodesType>[]
    paragraphNode: Partial<ContentItemViewNodesType> | null
    elementId: string
    /** textEl with annotation-only fields (optionItemState, changeType, etc.) stripped */
    textElBase: Record<string, unknown>
    beforeText: string
    afterText: string
    offset: number
    annotated: Array<{ text?: string | null }>
    newSelectionOptionId: string
    /** kpUserId of the inserting user; without it, a reject strikes the pill instead of removing it */
    authoredUserId?: string
}

/**
 * Pure tree transform: given cursor context and current nodes, returns the
 * updated node array with a new empty SelectionOption inserted at the cursor.
 *
 * Two paths:
 *  - Merge: cursor is at a boundary adjacent to an existing OptionGroup →
 *    append/prepend the new pill directly into that group.
 *  - Split: cursor is mid-text → split the text node, wrap the new pill in a
 *    new OptionGroup, and rebuild the paragraph's element list.
 */
export const buildNodesWithInsertedPill = ({
    currentNodes,
    paragraphNode,
    elementId,
    textElBase,
    beforeText,
    afterText,
    offset,
    annotated,
    newSelectionOptionId,
    authoredUserId = '',
}: BuildNodesParams): Partial<ContentItemViewNodesType>[] => {
    const newSelectionOption = {
        id: newSelectionOptionId,
        type: 'SelectionOption',
        text: '',
        changeType: 'ADDED' as const,
        authoredUserId,
        optionItemState: null,
        placeholderText: '',
        imperialUnits: '',
        metricUnits: '',
    }

    const paragraphElements = paragraphNode?.elements ?? []
    const currentIdx = paragraphElements.findIndex((el) => el.id === elementId)

    // currentIdx === -1 means the element is nested (e.g. a SelectionOption inside an OptionGroup).
    // This happens when the cursor is in an EmptyPlainTextContainer rendered after a SelectionOption —
    // its id is '{selectionOptionId}_additional', stripped to '{selectionOptionId}' by getBaseElementId.
    // In this case, insert the new pill directly into the parent OptionGroup after that sibling.
    if (currentIdx === -1) {
        const parentOG = paragraphElements.find((el) =>
            el.elements?.some((nested: Partial<ChildTextContentElement>) => nested.id === elementId)
        )
        if (parentOG?.id) {
            return currentNodes.map((node) => {
                if (node.id !== paragraphNode?.id) return node
                return {
                    ...node,
                    elements: (node.elements || []).map((el) => {
                        if (el.id !== parentOG.id) return el
                        const siblings = el.elements || []
                        const siblingIdx = siblings.findIndex((s: Partial<ChildTextContentElement>) => s.id === elementId)
                        const insertAt = siblingIdx >= 0 ? siblingIdx + 1 : siblings.length
                        return {
                            ...el,
                            elements: [
                                ...siblings.slice(0, insertAt),
                                newSelectionOption,
                                ...siblings.slice(insertAt),
                            ],
                        }
                    }),
                }
            })
        }
        return currentNodes
    }

    // The leading EmptyPlainTextContainer rendered in front of a paragraph that starts
    // with an OptionGroup (see NodeContentRenderer) reuses that OptionGroup's own id +
    // ADDITIONAL_ELEMENT_SUFFIX so getBaseElementId/getContentItemViewNodeByElementId can
    // resolve it back to the right paragraph. That means `elementId` here aliases the
    // OptionGroup's own id, not a Text element's — inserting via the normal split path
    // below would replace `paragraphElements[currentIdx]` and DROP the existing OptionGroup
    // (and its pill) entirely. Insert the new pill in a new OptionGroup immediately before
    // the existing one instead, leaving it untouched.
    if (paragraphElements[currentIdx]?.type === NODE_TYPES.OptionGroup) {
        const { before: beforeEls, after: afterEls } =
            splitAnnotatedElementsAtOffset(annotated, offset)

        const isBlank = (els: Array<{ text?: string | null }>) =>
            els.every((el) => isWhitespaceOnly(el.text || ''))

        // Nothing typed around the cursor — the new pill sits with no separating text
        // between it and the existing OptionGroup, so merge into it directly instead of
        // wrapping it in its own new OptionGroup next to the existing one.
        if (isBlank(beforeEls) && isBlank(afterEls)) {
            return currentNodes.map((node) => {
                if (node.id !== paragraphNode?.id) return node
                const idx = node.elements?.findIndex((el) => el.id === elementId) ?? -1
                if (idx === -1) return node
                return {
                    ...node,
                    elements: (node.elements || []).map((el, i) => {
                        if (i !== idx) return el
                        const siblings = el.elements || []
                        return { ...el, elements: [newSelectionOption, ...siblings] }
                    }),
                }
            })
        }

        const leadingBefore = beforeEls.map((el) => ({
            ...el,
            type: 'Text' as const,
            id: newGuid(),
        })) as TextContentElement[]

        const leadingAfter = (afterEls.length > 0
            ? afterEls.map((el) => ({
                  ...el,
                  type: 'Text' as const,
                  id: newGuid(),
              }))
            : [
                  {
                      id: newGuid(),
                      text: DEFAULT_TEXT_AFTER_PILL,
                      type: 'Text',
                      placeholderText: '',
                  },
              ]) as TextContentElement[]

        return currentNodes.map((node) => {
            if (node.id !== paragraphNode?.id) return node
            // Re-derive the index from this node's own elements (rather than reusing
            // `currentIdx`, which was computed from the separately-passed `paragraphNode`)
            // so this stays correct even if the two ever diverge, matching the pattern
            // the split path below uses.
            const idx = node.elements?.findIndex((el) => el.id === elementId) ?? -1
            if (idx === -1) return node
            return {
                ...node,
                elements: [
                    ...(node.elements || []).slice(0, idx),
                    ...leadingBefore,
                    {
                        id: newGuid(),
                        type: 'OptionGroup',
                        text: '',
                        changeType: 'ADDED' as const,
                        optionItemState: null,
                        elements: [newSelectionOption],
                    },
                    ...leadingAfter,
                    ...(node.elements || []).slice(idx),
                ],
            }
        })
    }

    const prevEl = currentIdx > 0 ? paragraphElements[currentIdx - 1] : null
    const nextEl =
        currentIdx < paragraphElements.length - 1
            ? paragraphElements[currentIdx + 1]
            : null
    const mergeWithPrev = isWhitespaceOnly(beforeText) && prevEl?.type === 'OptionGroup'
    const mergeWithNext = isWhitespaceOnly(afterText) && nextEl?.type === 'OptionGroup'

    if (mergeWithPrev || mergeWithNext) {
        const targetId = mergeWithPrev ? prevEl?.id : nextEl?.id
        // When flanked by OptionGroups on both sides, both sides are whitespace — clear entirely.
        const remainingText = mergeWithPrev && mergeWithNext ? '' : mergeWithPrev ? afterText : beforeText
        return currentNodes.map((node) => {
            if (node.id !== paragraphNode?.id) return node
            return {
                ...node,
                elements: (node.elements || []).map((el) => {
                    if (el.id === elementId) {
                        return { ...el, text: remainingText }
                    }
                    if (el.id !== targetId) return el
                    const siblings = el.elements || []
                    const newElements = mergeWithNext
                        ? [newSelectionOption, ...siblings]
                        : [...siblings, newSelectionOption]
                    return { ...el, elements: newElements }
                }),
            }
        })
    }

    // Split path: rebuild the paragraph around the cursor position.
    const { before: beforeEls, after: afterEls } =
        splitAnnotatedElementsAtOffset(annotated, offset)

    const mappedBefore = (beforeEls.length > 0
        ? beforeEls.map((el, i) => ({
              ...el,
              type: 'Text' as const,
              id: i === 0 ? elementId : newGuid(),
          }))
        : [{ ...textElBase, id: elementId, text: '' }]) as TextContentElement[]

    const mappedAfter = (afterEls.length > 0
        ? afterEls.map((el) => ({
              ...el,
              type: 'Text' as const,
              id: newGuid(),
          }))
        : [
              {
                  id: newGuid(),
                  text: DEFAULT_TEXT_AFTER_PILL,
                  type: 'Text',
                  placeholderText: '',
              },
          ]) as TextContentElement[]

    const newParagraphElements = [
        ...mappedBefore,
        {
            id: newGuid(),
            type: 'OptionGroup',
            text: '',
            changeType: 'ADDED' as const,
            optionItemState: null,
            elements: [newSelectionOption],
        },
        ...mappedAfter,
    ]

    return currentNodes.map((node) => {
        const idx = node.elements?.findIndex((el) => el.id === elementId) ?? -1
        if (idx === -1) return node
        return {
            ...node,
            elements: [
                ...(node.elements || []).slice(0, idx),
                ...newParagraphElements,
                ...(node.elements || []).slice(idx + 1),
            ],
        }
    })
}

/**
 * Type guard to check if an element is a ReplacementOption
 */
const isReplacementOption = (
    element: ChildTextContentElement | TextContentElement
): boolean => {
    return element?.type === NODE_TYPES.ReplacementOption
}

/**
 * Type guard to check if an element is a SelectionOption
 */
const isSelectionOption = (element: ChildTextContentElement): boolean => {
    return element.type === NODE_TYPES.SelectionOption
}

/**
 * Processes non-target ReplacementOption elements
 * @param element - The element to process
 * @returns Updated element with appropriate state
 */
const handleNonTargetReplacementOption = (
    element: ChildTextContentElement
): ChildTextContentElement => {
    const changeType = element.changeType?.toUpperCase()

    // Preserve elements that are already modified
    if (changeType === PillTrackChangeState.Modified) {
        return element
    }

    // Set elements to InActive/Disabled if they have no changeType or are reset options
    if (!changeType || changeType === PillTrackChangeState.ResetOption) {
        const hasCustomContent = Boolean(
            element.text?.trim() &&
                element.placeholderText?.trim() &&
                element.text.trim() !== element.placeholderText.trim()
        )
        if (hasCustomContent) {
            return element
        }
        return {
            ...element,
            optionItemState: PillStateVariants.InActive,
            changeType: PillTrackChangeState.Disabled as 'MODIFIED',
            text: removeUnitOfMeasureDelimiters(element.placeholderText),
        }
    }

    // Return unchanged for other change types
    return element
}

/**
 * Handles updates for ReplacementOption elements with improved separation of concerns
 */
const handleReplacementOptionUpdate = (
    element: ChildTextContentElement,
    isTarget: boolean,
    updatedContentItem: TextContentElement
): ChildTextContentElement => {
    // Delegate non-target processing to dedicated function
    if (!isTarget) {
        return isContentUnmodified(updatedContentItem)
            ? element
            : handleNonTargetReplacementOption(element)
    }

    // Process target element with extracted helper functions
    return handleTargetReplacementOption(element, updatedContentItem)
}

/**
 * Determines if content is unmodified by comparing with placeholder text
 * @param updatedContentItem - The content item to check
 * @returns True if content matches placeholder (unmodified)
 */
const isContentUnmodified = (
    updatedContentItem: TextContentElement
): boolean => {
    return (
        updatedContentItem?.placeholderText != null &&
        updatedContentItem?.text != null &&
        updatedContentItem.placeholderText.trim() ===
            updatedContentItem.text.trim()
    )
}

/**
 * Calculates the new state for a target ReplacementOption element
 * @param currentState - Current option item state
 * @param isUnmodified - Whether content is unmodified
 * @returns New option item state
 */
const calculateTargetReplacementState = (
    currentState: OptionItemState,
    isUnmodified: boolean
): OptionItemState => {
    const isCurrentlyOptedIn = currentState === PillStateVariants.OptedIn
    const isCurrentlyInActive = currentState === PillStateVariants.InActive

    if (isUnmodified && !isCurrentlyInActive) {
        return PillStateVariants.InActive
    }

    if (isCurrentlyOptedIn || isCurrentlyInActive) {
        return null // Toggle InActive to no explicit state
    }

    return PillStateVariants.OptedIn
}

/**
 * Determines the appropriate change type for a target ReplacementOption
 * @param element - The element being updated
 * @param newState - The new option item state
 * @returns Appropriate change type
 */
const getTargetReplacementChangeType = (
    element: ChildTextContentElement,
    newState: OptionItemState
): string | null => {
    const isCurrentlyModified =
        element.changeType?.toUpperCase() === PillTrackChangeState.Modified
    const isCurrentlyInActive =
        element.optionItemState === PillStateVariants.InActive
    const isCurrentlyOptedIn =
        element.optionItemState === PillStateVariants.OptedIn

    if (isCurrentlyModified) {
        return PillTrackChangeState.ResetOption
    }

    if (isCurrentlyInActive) {
        return null
    }

    if (isCurrentlyOptedIn) {
        return PillTrackChangeState.Disabled
    }

    return PillTrackChangeState.Modified
}

/**
 * Processes target ReplacementOption elements
 * @param element - The target element to process
 * @param updatedContentItem - Updated content data
 * @returns Updated element with new state and change type
 */
const handleTargetReplacementOption = (
    element: ChildTextContentElement,
    updatedContentItem: TextContentElement
): ChildTextContentElement => {
    const isUnmodified = isContentUnmodified(updatedContentItem)

    // If updatedContentItem explicitly sets optionItemState, use it
    // Otherwise calculate based on current state
    const newState =
        updatedContentItem.optionItemState !== undefined
            ? updatedContentItem.optionItemState
            : isUnmodified
            ? null
            : calculateTargetReplacementState(
                  element.optionItemState,
                  isUnmodified
              )

    let newChangeType = getTargetReplacementChangeType(element, newState)
    const hasUOMElement = hasUOMContent(updatedContentItem)

    if (hasUOMElement) {
        newChangeType = 'MODIFIED'
    }

    // Clean UOM delimiters from text if present
    const cleanedText = updatedContentItem.text
        ? removeUnitOfMeasureDelimiters(updatedContentItem.text)
        : updatedContentItem.text

    return {
        ...element, // Preserve original element properties
        ...updatedContentItem, // Apply updates from updatedContentItem
        text: cleanedText, // Use cleaned text
        optionItemState: newState,
        changeType: newChangeType as 'MODIFIED',
        elements:
            newChangeType === 'MODIFIED'
                ? updatedContentItem.elements ?? []
                : element.elements,
    }
}

/**
 * Determines the appropriate state for a SelectionOption element
 */
const getSelectionOptionState = (
    element: ChildTextContentElement,
    isTarget: boolean,
    value: OptionItemState
): OptionItemState => {
    if (isTarget) {
        return value
    }

    const isCurrentlyOptedIn =
        element.optionItemState === PillStateVariants.OptedIn
    return isCurrentlyOptedIn
        ? PillStateVariants.OptedIn
        : PillStateVariants.InActive
}

/**
 * Determines the appropriate change type for a SelectionOption element
 * @param newState - The new state being applied
 * @returns Appropriate change type for the new state
 */
const getSelectionOptionChangeType = (
    newState?: string | null
): string | null => {
    if (!newState) return null

    return newState === PillStateVariants.InActive
        ? PillTrackChangeState.Disabled
        : PillTrackChangeState.Modified
}

/**
 * Handles updates for SelectionOption elements
 */
const handleSelectionOptionUpdate = (
    element: ChildTextContentElement,
    isTarget: boolean,
    value: OptionItemState,
    targetElement?: ChildTextContentElement,
    updatedData?: TextContentElement
): ChildTextContentElement => {
    // New pills (user-inserted) preserve their ADDED changeType and null optionItemState
    // so the server treats them as insertions, not modifications.
    // Also carry forward any text typed in the inline input.
    if (element.changeType === PillTrackChangeState.Added) {
        return {
            ...element,
            optionItemState: null,
            ...(isTarget && updatedData?.text !== undefined ? { text: updatedData.text } : {}),
        }
    }

    const isResetTriggeredForReplacementOption =
        targetElement?.type === 'ReplacementOption' &&
        targetElement?.placeholderText?.trim() === targetElement?.text?.trim()
    const newState = isResetTriggeredForReplacementOption
        ? element.optionItemState
        : getSelectionOptionState(element, isTarget, value)
    const newChangeType = getSelectionOptionChangeType(newState)

    return {
        ...element,
        optionItemState: newState,
        changeType: newChangeType as 'MODIFIED',
    }
}

/**
 * Updates elements within an OptionGroup.
 * For OptionGroups: set target element as active with the specified value, others as inactive.
 */
export const handleOptionGroupUpdate = (
    elements: ChildTextContentElement[],
    updatedContentItem: TextContentElement,
    targetId: string,
    value: OptionItemState
): ChildTextContentElement[] => {
    // Input validation
    if (!elements?.length || !targetId || !value) {
        return elements || []
    }

    if (!updatedContentItem) {
        return elements
    }

    // handlePillClick fires on inline-edit blur before the mutation clears changeType —
    // only update the target's text; do not run sibling deactivation for ADDED pills.
    if (updatedContentItem.changeType === PillTrackChangeState.Added) {
        return elements.map((element) => {
            if (!element?.id || element.id !== targetId) return element
            return isSelectionOption(element)
                ? handleSelectionOptionUpdate(element, true, value, undefined, updatedContentItem)
                : element
        })
    }

    try {
        const updatedElements = elements.map((element) => {
            if (!element?.id) {
                return element
            }

            const isTarget = element.id === targetId

            // Handle ReplacementOption
            if (isReplacementOption(element)) {
                return handleReplacementOptionUpdate(
                    element,
                    isTarget,
                    updatedContentItem
                )
            }

            // Handle SelectionOption
            if (isSelectionOption(element)) {
                return handleSelectionOptionUpdate(
                    element,
                    isTarget,
                    value,
                    updatedContentItem?.type === 'ReplacementOption'
                        ? updatedContentItem
                        : undefined,
                    isTarget ? updatedContentItem : undefined
                )
            }

            // No changes for other types
            return element
        })

        return updatedElements
    } catch (error) {
        return elements
    }
}

/**
 * Utility function to validate option item state
 */
export const isValidOptionItemState = (state: string) => {
    return Object.values(PillStateVariants).includes(state as 'OptedIn')
}

/**
 * Utility function to get all elements of a specific type
 */
export const getElementsByType = (
    elements: ChildTextContentElement[],
    type: string
): ChildTextContentElement[] => {
    return elements.filter((element) => element.type === type)
}
